import { Injectable } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { tap, catchError, map, switchMap } from 'rxjs/operators';
import { ApiService } from '@core/api/api.service';
import { EnvConfig } from '@core/config';
import {
  FundDDQRun,
  CreateFundDDQRunRequest,
  IssuersSearchResponse,
  FundsSearchResponse,
  IssuerAdminsSearchResponse,
  CreateFundApiRequest,
  CreateFundApiResponse,
  InviteIssuerAdminApiRequest,
  PaginatedFundsResponse,
  LPInviteRequest,
  GPInviteRequest,
  VerifyInvestorRequest,
  VerifyInvestorResponse,
} from '@core/models/fund-ddq-run.model';
import { FundDDQRunStore } from './fund-ddq-run.store';
import { PaginationParams } from '@core/api/api-entity.service';
import { HttpParams } from '@angular/common/http';
import {
  FundDDQRunListFilterParams,
  buildFundDDQRunListCacheKey,
  toFundDDQRunApiFilter,
} from './fund-ddq-run-filter';

export { FUND_DDQ_RUN_FILTER, DEFAULT_FUND_DDQ_RUN_RECENT_DAYS } from './fund-ddq-run-filter';
export type { FundDDQRunFilter, FundDDQRunListFilterParams } from './fund-ddq-run-filter';

export interface IssuerSearchResult {
  uid: string;
  name: string;
}

export interface FundSearchParams {
  issuerUid?: string;
  query?: string;
  /**
   * AssetClass.codename filter (e.g. 'direct-deal'). Optional; omitted = all classes.
   */
  assetClass?: string;
}

export interface FundSearchResult {
  productUid: string;
  productName: string;
  issuerUid: string;
  issuerCompanyUid?: string;
  issuerCompanyName: string;
}

export interface IssuerAdminSearchResult {
  issuerUid: string;
  issuerCompanyName: string;
  emittentUserUid: string;
  userFullName: string;
  email: string;
  investorType?: string;
  fullCompanyName?: string;
}

export interface IssuerAdminSearchParams {
  issuerUid: string;
  productUid: string;
  /**
   * If provided, backend can filter admins by email.
   * If omitted, backend should return all admins for issuer+product.
   */
  email?: string;
}

export interface IssuerAdminInviteRequest {
  issuerUid: string;
  productUid: string;
  userUid: string;
}

export interface IssuerAdminInviteByEmailRequest {
  issuerUid: string;
  productUid: string;
  email: string;
}

export interface InviteIssuerFromFundRequest {
  issuerUid: string;
  productUid: string;
  email?: string;
  userUid?: string;
}

export interface InitializeEmittentDefaultTemplatesPayload {
  partner_uid: string;
  client_scope_uid: string;
  emittent_uid: string;
  product_uid: string;
  created_by_uid: string;
}

function mapApiRecordToFundDDQRun(item: unknown): FundDDQRun {
  const r = item as {
    issuer_company_name?: string;
    issuerCompanyName?: string;
    issuer_uid?: string;
    issuerUid?: string;
    product_name?: string;
    productName?: string;
    product_uid?: string;
    productUid?: string;
    ddq_run_count?: number;
    ddqRunCount?: number;
    latestDdqRunCreatedAt?: string | null;
    can_access_fund?: boolean;
    canAccessFund?: boolean;
  };
  const latestAt = r.latestDdqRunCreatedAt ?? null;
  return {
    issuerCompanyName: r.issuer_company_name ?? r.issuerCompanyName ?? '',
    issuerUid: r.issuer_uid ?? r.issuerUid ?? '',
    productName: r.product_name ?? r.productName ?? '',
    productUid: r.product_uid ?? r.productUid ?? '',
    ddqRunCount: r.ddq_run_count ?? r.ddqRunCount ?? 0,
    latestDdqRunCreatedAt: latestAt === undefined ? null : latestAt,
    canAccessFund: r.can_access_fund ?? r.canAccessFund ?? true,
  };
}

@Injectable({ providedIn: 'root' })
export class FundDDQRunService {
  private readonly fundsUrl = 'subscriptions/funds/';
  private readonly issuersSearchUrl = 'subscriptions/issuers/search/';
  private readonly fundsSearchUrl = 'subscriptions/funds/search/';
  private readonly issuerAdminSearchUrl = 'subscriptions/issuers/admins/search/';
  private readonly issuerInviteUrl = 'subscriptions/issuers/invite/';
  private readonly issuerInviteByEmailUrl = 'subscriptions/issuers/invite-by-email/';
  private readonly lpInviteUrl = 'subscriptions/invitations/ddq/lp/';
  private readonly gpInviteUrl = 'subscriptions/invitations/ddq/gp/';
  private readonly verifyInvestorUrl = 'subscriptions/verify/investor/';
  private readonly initializeEmittentDefaultTemplatesUrl = '/sp_initialize_emittent_default_templates';
  private lastFetchTime: number = 0;
  private lastCacheKey = '';
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  /** Max issuers to expand when searching by issuer name. */
  private readonly MAX_ISSUER_SEARCH_MATCHES = 15;
  /** Page size used when collecting issuer-linked funds for search merge. */
  private readonly SEARCH_COLLECTION_PAGE_SIZE = 100;

  constructor(
    private apiService: ApiService,
    private store: FundDDQRunStore,
    private env: EnvConfig,
  ) {}

  public initializeEmittentDefaultTemplates(
    payload: InitializeEmittentDefaultTemplatesPayload,
  ): Observable<unknown> {
    return this.apiService.post(`${this.env.aiEngineApiUrl || ''}${this.initializeEmittentDefaultTemplatesUrl}`, payload);
  }

  /**
   * Get all Fund DDQ Runs with pagination, search, and optional backend filters
   * @param paginationParams Pagination parameters (page, pageSize)
   * @param search Search term for filtering results
   * @param forceRefresh Force refresh from API, bypassing cache
   * @param filterParams Backend list filters (`filter`)
   */
  getFundDDQRuns(
    paginationParams?: PaginationParams,
    search?: string,
    forceRefresh: boolean = false,
    filterParams?: FundDDQRunListFilterParams,
  ): Observable<FundDDQRun[]> {
    const now = Date.now();
    const hasCachedData = this.store.getValue().fundDDQRuns.length > 0;
    const isCacheValid = now - this.lastFetchTime < this.CACHE_DURATION;
    const cacheKey = buildFundDDQRunListCacheKey(paginationParams, search, filterParams);
    const currentPagination = this.store.getValue().pagination;
    const searchTerm = search?.trim() || '';

    // Return cached data if available, valid, not forcing refresh, pagination hasn't changed,
    // search hasn't changed, and the filter cache key matches.
    // Note: When search is active, we should always fetch fresh data
    if (
      !forceRefresh &&
      hasCachedData &&
      isCacheValid &&
      !paginationParams &&
      !searchTerm &&
      currentPagination &&
      this.lastCacheKey === cacheKey
    ) {
      return of(this.store.getValue().fundDDQRuns);
    }

    if (this.lastCacheKey && this.lastCacheKey !== cacheKey) {
      this.store.setFundDDQRuns([], null);
    }

    this.store.setLoading(true);

    const request$ = searchTerm
      ? this.fetchFundDDQRunsMatchingSearch(paginationParams, searchTerm, filterParams)
      : this.fetchFundDDQRunsPage(paginationParams, undefined, filterParams).pipe(
          map((response) => this.applyFundDDQRunsResponse(response, paginationParams)),
        );

    return request$.pipe(
      tap(() => {
        this.lastFetchTime = Date.now();
        this.lastCacheKey = cacheKey;
      }),
      catchError((error: unknown) => {
        this.store.setErrorState('Failed to load Fund DDQ Runs');
        console.error('Error loading Fund DDQ Runs:', error);
        return of([]);
      }),
    );
  }

  /**
   * Search matches either issuer company name or fund/product name.
   * The list API `search` param alone is not enough for issuer-only matches, so we
   * also resolve issuers by name and merge their funds, then paginate client-side.
   */
  private fetchFundDDQRunsMatchingSearch(
    paginationParams: PaginationParams | undefined,
    searchTerm: string,
    filterParams?: FundDDQRunListFilterParams,
  ): Observable<FundDDQRun[]> {
    const collectionPagination: PaginationParams = {
      page: 1,
      pageSize: this.SEARCH_COLLECTION_PAGE_SIZE,
    };

    const byFundOrApiSearch$ = this.fetchFundDDQRunsPage(collectionPagination, searchTerm, filterParams).pipe(
      map((response: PaginatedFundsResponse | { funds: unknown[] }) => this.mapFundsResponseToRuns(response)),
      catchError(() => of([] as FundDDQRun[])),
    );

    const byIssuerName$ = this.searchIssuers(searchTerm).pipe(
      switchMap((issuers: IssuerSearchResult[]) => {
        const issuerUids = issuers
          .map((issuer: IssuerSearchResult) => issuer.uid)
          .filter((uid: string) => !!uid)
          .slice(0, this.MAX_ISSUER_SEARCH_MATCHES);

        if (issuerUids.length === 0) {
          return of([] as FundDDQRun[]);
        }

        return forkJoin(
          issuerUids.map((issuerUid: string) =>
            this.fetchFundDDQRunsPage(collectionPagination, undefined, filterParams, issuerUid).pipe(
              map((response: PaginatedFundsResponse | { funds: unknown[] }) => this.mapFundsResponseToRuns(response)),
              catchError(() => of([] as FundDDQRun[])),
            ),
          ),
        ).pipe(map((pages: FundDDQRun[][]) => pages.flat()));
      }),
      catchError(() => of([] as FundDDQRun[])),
    );

    return forkJoin([byFundOrApiSearch$, byIssuerName$]).pipe(
      map(([apiSearchRuns, issuerRuns]: [FundDDQRun[], FundDDQRun[]]) => {
        const merged = this.dedupeFundDDQRunsByProductUid([...apiSearchRuns, ...issuerRuns]);
        const matched = merged.filter((run) => this.matchesIssuerOrFundName(run, searchTerm));
        return this.paginateAndStoreFundDDQRuns(matched, paginationParams);
      }),
    );
  }

  private fetchFundDDQRunsPage(
    paginationParams?: PaginationParams,
    search?: string,
    filterParams?: FundDDQRunListFilterParams,
    issuerUid?: string,
  ): Observable<PaginatedFundsResponse | { funds: unknown[] }> {
    let httpParams = new HttpParams();
    if (paginationParams) {
      if (paginationParams.page) {
        httpParams = httpParams.set('pagination.page', paginationParams.page.toString());
      }
      if (paginationParams.pageSize) {
        httpParams = httpParams.set('pagination.page_size', paginationParams.pageSize.toString());
      }
    }
    if (search && search.trim()) {
      httpParams = httpParams.set('search', search.trim());
    }
    if (issuerUid?.trim()) {
      httpParams = httpParams.set('issuer_uid', issuerUid.trim());
    }

    const apiFilter = toFundDDQRunApiFilter(filterParams?.filter);
    if (apiFilter) {
      httpParams = httpParams.set('filter', apiFilter);
    }

    return this.apiService.get<PaginatedFundsResponse>(this.fundsUrl, { params: httpParams });
  }

  private applyFundDDQRunsResponse(
    response: PaginatedFundsResponse | { funds: unknown[] },
    paginationParams?: PaginationParams,
  ): FundDDQRun[] {
    if (response && (response as PaginatedFundsResponse).results && Array.isArray((response as PaginatedFundsResponse).results)) {
      const paginated = response as PaginatedFundsResponse;
      const pagination: PaginationParams = {
        page: paginated.pagination?.page || paginationParams?.page || 1,
        pageSize: paginated.pagination?.page_size || paginationParams?.pageSize || 10,
        total: paginated.pagination?.total || 0,
      };
      const runs = paginated.results.map((row) => mapApiRecordToFundDDQRun(row));
      this.store.setFundDDQRuns(runs, pagination);
      return runs;
    }
    // Fallback for non-paginated response (backward compatibility)
    if (response && (response as any).funds && Array.isArray((response as any).funds)) {
      const funds = (response as any).funds.map((row: unknown) => mapApiRecordToFundDDQRun(row));
      this.store.setFundDDQRuns(funds, null);
      return funds;
    }
    this.store.setFundDDQRuns([], null);
    return [];
  }

  private mapFundsResponseToRuns(response: PaginatedFundsResponse | { funds: unknown[] }): FundDDQRun[] {
    if (response && (response as PaginatedFundsResponse).results && Array.isArray((response as PaginatedFundsResponse).results)) {
      return (response as PaginatedFundsResponse).results.map((row) => mapApiRecordToFundDDQRun(row));
    }
    if (response && (response as any).funds && Array.isArray((response as any).funds)) {
      return (response as any).funds.map((row: unknown) => mapApiRecordToFundDDQRun(row));
    }
    return [];
  }

  private dedupeFundDDQRunsByProductUid(runs: FundDDQRun[]): FundDDQRun[] {
    const byProductUid = new Map<string, FundDDQRun>();
    runs.forEach((run) => {
      if (!run?.productUid || byProductUid.has(run.productUid)) {
        return;
      }
      byProductUid.set(run.productUid, run);
    });
    return Array.from(byProductUid.values());
  }

  private matchesIssuerOrFundName(run: FundDDQRun, searchTerm: string): boolean {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return true;
    }
    return (
      (run.issuerCompanyName || '').toLowerCase().includes(term) ||
      (run.productName || '').toLowerCase().includes(term)
    );
  }

  private paginateAndStoreFundDDQRuns(
    runs: FundDDQRun[],
    paginationParams?: PaginationParams,
  ): FundDDQRun[] {
    const page = paginationParams?.page || 1;
    const pageSize = paginationParams?.pageSize || 10;
    const start = Math.max(0, (page - 1) * pageSize);
    const pageRows = runs.slice(start, start + pageSize);
    this.store.setFundDDQRuns(pageRows, {
      page,
      pageSize,
      total: runs.length,
    });
    return pageRows;
  }

  /**
   * Get a single Fund DDQ Run by product UID
   */
  getFundDDQRun(productUid: string): Observable<FundDDQRun | null> {
    // Check cache first
    const cachedRun = this.store.getValue().fundDDQRuns.find((r) => r.productUid === productUid);
    if (cachedRun) {
      return of(cachedRun);
    }

    // If not in cache, search in the list
    return this.getFundDDQRuns().pipe(
      map((runs) => {
        const found = runs.find((r) => r.productUid === productUid);
        return found || null;
      }),
      catchError((error) => {
        console.error('Error loading Fund DDQ Run:', error);
        return of(null);
      }),
    );
  }

  /**
   * Search issuers by name
   * @param name Issuer name to search for
   */
  searchIssuers(name: string): Observable<IssuerSearchResult[]> {
    if (!name || name.trim().length === 0) {
      return of([]);
    }

    const params = new HttpParams().set('name', name.trim());

    return this.apiService.get<IssuersSearchResponse>(this.issuersSearchUrl, { params }).pipe(
      map((response: IssuersSearchResponse) => {
        // API returns { issuers: [...] }
        if (response && response.issuers && Array.isArray(response.issuers)) {
          return response.issuers.map((issuer) => ({
            uid: issuer.uid,
            name: issuer.issuerCompanyName || issuer.issuer_company_name || issuer.name || '',
          }));
        }
        return [];
      }),
      catchError((error) => {
        console.error('Error searching issuers:', error);
        return of([]);
      }),
    );
  }

  /**
   * Search funds
   * @param params Search parameters (issuerUid and/or query)
   */
  searchFunds(params: FundSearchParams): Observable<FundDDQRun[]> {
    let httpParams = new HttpParams();

    if (params.issuerUid) {
      httpParams = httpParams.set('issuer_uid', params.issuerUid);
    }

    if (params.query) {
      httpParams = httpParams.set('query', params.query.trim());
    }

    if (params.assetClass?.trim()) {
      httpParams = httpParams.set('asset_class', params.assetClass.trim());
    }

    // Don't make request if no search parameters provided
    if (!params.issuerUid && !params.query) {
      return of([]);
    }

    this.store.setLoading(true);

    return this.apiService.get<FundsSearchResponse>(this.fundsSearchUrl, { params: httpParams }).pipe(
      map((response: FundsSearchResponse) => {
        // API returns { products: [...] }
        if (response && response.products && Array.isArray(response.products)) {
          return response.products.map((product) => ({
            issuerCompanyName: product.issuer_company_name || product.issuerCompanyName || '',
            issuerUid: product.issuer_uid || product.issuerUid || '',
            productName: product.product_name || product.productName || '',
            productUid: product.product_uid || product.productUid || '',
            ddqRunCount: product.ddq_run_count || product.ddqRunCount || 0,
          }));
        }
        return [];
      }),
      tap((runs) => {
        this.store.setFundDDQRuns(runs);
      }),
      catchError((error) => {
        this.store.setErrorState('Failed to search funds');
        console.error('Error searching funds:', error);
        return of([]);
      }),
    );
  }

  /**
   * Create a new Fund DDQ Run
   * Uses /api/subscriptions/funds/create/ or /api/subscriptions/funds/create-by-issuer/
   *
   * Endpoint selection:
   * - /funds/create-by-issuer/ → when issuerUid is available (existing issuer)
   * - /funds/create/ → when issuerUid is NOT available but issuerName is available (new issuer)
   */
  createFundDDQRun(request: CreateFundDDQRunRequest): Observable<FundDDQRun> {
    this.store.setLoading(true);

    // Determine which endpoint to use based on issuerUid availability:
    // - useCreateByIssuer === true  -> /funds/create-by-issuer/ (requires issuer_uid)
    // - otherwise                   -> /funds/create/ (requires issuer_name, does NOT accept issuer_uid)
    const endpoint = request.useCreateByIssuer
      ? 'subscriptions/funds/create-by-issuer/'
      : 'subscriptions/funds/create/';

    // Map request to API format
    const apiRequest: CreateFundApiRequest = {
      fund_name: request.fundName,
    };

    // For /funds/create-by-issuer/: require issuer_uid
    if (request.useCreateByIssuer && request.issuerUid) {
      apiRequest.issuer_uid = request.issuerUid;
    }
    // For /funds/create/: require issuer_name (and do NOT send issuer_uid)
    else if (!request.useCreateByIssuer && request.issuerName) {
      apiRequest.issuer_name = request.issuerName;
    }

    // Include asset_class and asset_subclass if provided (optional fields)
    if (request.assetClass) {
      apiRequest.asset_class = request.assetClass;
    }
    if (request.assetSubclass) {
      apiRequest.asset_subclass = request.assetSubclass;
    }
    if (request.keepPrivate != null) {
      apiRequest.keep_private = !!request.keepPrivate;
    }
    if (request.keepPrivate && request.clientCompany) {
      apiRequest.client_company = request.clientCompany;
    }

    return this.apiService.post<CreateFundApiResponse>(endpoint, apiRequest).pipe(
      map((response: CreateFundApiResponse) => {
        // Map API response to FundDDQRun format
        return {
          issuerCompanyName: response.issuer_company_name || response.issuerCompanyName || '',
          issuerUid: response.issuer_uid || response.issuerUid || '',
          productName: response.product_name || response.productName || '',
          productUid: response.product_uid || response.productUid || '',
          ddqRunCount: 0, // New fund has no DDQ runs yet
          latestDdqRunCreatedAt: null,
        };
      }),
      tap((run) => {
        this.lastFetchTime = 0; // Invalidate cache
        this.lastCacheKey = '';
        this.store.addFundDDQRun(run);
      }),
      catchError((error) => {
        this.store.setErrorState('Failed to create Fund DDQ Run');
        console.error('Error creating Fund DDQ Run:', error);
        throw error;
      }),
    );
  }

  /**
   * Search funds for dropdown (returns simplified FundSearchResult[])
   * @param params Search parameters (query is required, issuerUid is optional)
   */
  searchFundsForDropdown(params: FundSearchParams): Observable<FundSearchResult[]> {
    // Query is required, issuerUid is optional (backend supports both)
    if (!params.query || params.query.trim().length === 0) {
      return of([]);
    }

    let httpParams = new HttpParams().set('query', params.query.trim());

    // issuerUid is optional - if provided, filter by issuer; otherwise search all funds
    if (params.issuerUid) {
      httpParams = httpParams.set('issuer_uid', params.issuerUid);
    }

    // Optional AssetClass.codename filter (case-insensitive exact match on backend)
    if (params.assetClass?.trim()) {
      httpParams = httpParams.set('asset_class', params.assetClass.trim());
    }

    return this.apiService.get<FundsSearchResponse>(this.fundsSearchUrl, { params: httpParams }).pipe(
      map((response: FundsSearchResponse) => {
        // API returns { products: [...] }
        if (response && response.products && Array.isArray(response.products)) {
          return response.products.map((product) => ({
            productUid: product.product_uid || product.productUid || '',
            productName: product.product_name || product.productName || '',
            issuerUid: product.issuer_uid || product.issuerUid || '',
            issuerCompanyUid: product.issuer_company_uid || product.issuerCompanyUid || '',
            issuerCompanyName: product.issuer_company_name || product.issuerCompanyName || '',
          }));
        }
        return [];
      }),
      catchError((error) => {
        console.error('Error searching funds:', error);
        return of([]);
      }),
    );
  }

  /**
   * Search issuer admins
   * @param params Search parameters (issuerUid, productUid, email)
   */
  searchIssuerAdmins(params: IssuerAdminSearchParams): Observable<IssuerAdminSearchResult[]> {
    if (!params.issuerUid || !params.productUid) {
      return of([]);
    }

    const httpParams = new HttpParams()
      .set('issuer_uid', params.issuerUid)
      .set('product_uid', params.productUid);

    if (params.email && params.email.trim().length > 0) {
      httpParams.set('email', params.email.trim());
    }

    return this.apiService.get<IssuerAdminsSearchResponse>(this.issuerAdminSearchUrl, { params: httpParams }).pipe(
      map((response: IssuerAdminsSearchResponse) => {
        // API returns { users: [...] }
        if (response && response.users && Array.isArray(response.users)) {
          return response.users.map((user) => ({
            issuerUid: user.issuer_uid || user.issuerUid || '',
            issuerCompanyName: user.issuer_company_name || user.issuerCompanyName || '',
            emittentUserUid: user.emittent_user_uid || user.emittentUserUid || '',
            userFullName: user.user_full_name || user.userFullName || '',
            email: user.email || '',
          }));
        }
        return [];
      }),
      catchError((error) => {
        console.error('Error searching issuer admins:', error);
        return of([]);
      }),
    );
  }

  /**
   * Invite existing issuer admin
   * @param request Invite request (issuerUid, productUid, userUid)
   */
  inviteIssuerAdmin(request: IssuerAdminInviteRequest): Observable<unknown> {
    if (!request.issuerUid || !request.productUid || !request.userUid) {
      return of({ error: 'Missing required parameters' });
    }

    const apiRequest: InviteIssuerAdminApiRequest = {
      issuer_uid: request.issuerUid,
      product_uid: request.productUid,
      user_uid: request.userUid,
    };

    return this.apiService.post<unknown>(this.issuerInviteUrl, apiRequest).pipe(
      map((response: unknown) => response),
      catchError((error) => {
        console.error('Error inviting issuer admin:', error);
        throw error;
      }),
    );
  }

  /**
   * Invite issuer admin by email
   * @param request Invite request (issuerUid, productUid, email)
   */
  inviteIssuerAdminByEmail(request: IssuerAdminInviteByEmailRequest): Observable<unknown> {
    if (!request.issuerUid || !request.productUid || !request.email) {
      return of({ error: 'Missing required parameters' });
    }

    const apiRequest: InviteIssuerAdminApiRequest = {
      issuer_uid: request.issuerUid,
      product_uid: request.productUid,
      email: request.email.trim(),
    };

    return this.apiService.post<unknown>(this.issuerInviteByEmailUrl, apiRequest).pipe(
      map((response: unknown) => response),
      catchError((error) => {
        console.error('Error inviting issuer admin by email:', error);
        throw error;
      }),
    );
  }

  /**
   * Invite issuer admin from fund DDQ run row data
   * Constructs the invite request from FundDDQRun data
   * Note: This requires either email or userUid to be provided
   * @deprecated Use inviteLP or inviteGP instead based on user type
   * @param run Fund DDQ run row data
   * @param email Optional email address (required if userUid not provided)
   * @param userUid Optional user UID (required if email not provided)
   */
  inviteIssuerFromFund(run: FundDDQRun, email?: string, userUid?: string): Observable<unknown> {
    if (!run.issuerUid || !run.productUid) {
      return of({ error: 'Missing issuerUid or productUid in fund data' });
    }

    if (email) {
      return this.inviteIssuerAdminByEmail({
        issuerUid: run.issuerUid,
        productUid: run.productUid,
        email: email,
      });
    } else if (userUid) {
      return this.inviteIssuerAdmin({
        issuerUid: run.issuerUid,
        productUid: run.productUid,
        userUid: userUid,
      });
    } else {
      return of({ error: 'Either email or userUid is required' });
    }
  }

  /**
   * Invite LP (Limited Partner) user for DDQ
   * POST /api/subscriptions/invitations/ddq/lp/
   * @param request LP invitation request
   */
  inviteLP(request: LPInviteRequest): Observable<unknown> {
    if (!request.invitee_email) {
      return of({ error: 'Missing required parameter: invitee_email' });
    }

    // Validate that at least one of issuer_name or existing_issuer_uid is provided
    if (!request.issuer_name && !request.existing_issuer_uid) {
      return of({ error: 'Either issuer_name or existing_issuer_uid is required' });
    }

    return this.apiService.post<unknown>(this.lpInviteUrl, request).pipe(
      map((response: unknown) => response),
      catchError((error) => {
        console.error('Error inviting LP user:', error);
        throw error;
      }),
    );
  }

  /**
   * Invite GP (General Partner) user for DDQ
   * POST /api/subscriptions/invitations/ddq/gp/
   * @param request GP invitation request
   */
  inviteGP(request: GPInviteRequest): Observable<unknown> {
    if (!request.invitee_email) {
      return of({ error: 'Missing required parameter: invitee_email' });
    }

    return this.apiService.post<unknown>(this.gpInviteUrl, request).pipe(
      map((response: unknown) => response),
      catchError((error) => {
        console.error('Error inviting GP user:', error);
        throw error;
      }),
    );
  }

  /**
   * Verify investor by name and email
   * GET /api/subscriptions/verify/investor/?investor_name=...&investor_email=...
   * Used for emittent organizations to verify investor details before inviting
   * Note: investor_name can be empty string when searching by email only
   * @param request Verify investor request (investor_name can be empty, investor_email is required)
   */
  verifyInvestor(request: VerifyInvestorRequest): Observable<VerifyInvestorResponse> {
    if (!request.investor_email) {
      return of({ error: 'Missing required parameter: investor_email' } as any);
    }

    const httpParams = new HttpParams().set('investor_email', request.investor_email.trim());

    // Add investor_name only if provided (can be empty string for email-only search)
    if (request.investor_name !== undefined && request.investor_name !== null) {
      httpParams.set('investor_name', request.investor_name.trim());
    }

    return this.apiService.get<VerifyInvestorResponse>(this.verifyInvestorUrl, { params: httpParams }).pipe(
      map((response: VerifyInvestorResponse) => response),
      catchError((error) => {
        console.error('Error verifying investor:', error);
        throw error;
      }),
    );
  }
}
